import React, { useState, useEffect } from "react";
import {
  Modal,
  Textarea,
  TextInput,
  Button,
  Stack,
  Text,
  Group,
  Alert,
} from "@mantine/core";
import { httpsCallable } from "firebase/functions";
import { functions } from "../../firebase";

export interface RequestOnboardingHelpModalProps {
  opened: boolean;
  onClose: () => void;
  source: "registration_flow" | "dashboard_help" | "ai_onboarding_wizard";
  defaultName?: string;
  defaultEmail?: string;
  defaultPhone?: string;
  companyId?: string;
  companyName?: string;
  draftId?: string | null;
  conversationId?: string | null;
}

export function RequestOnboardingHelpModal({
  opened,
  onClose,
  source,
  defaultName = "",
  defaultEmail = "",
  defaultPhone = "",
  companyId = "",
  companyName = "",
  draftId = null,
  conversationId = null,
}: RequestOnboardingHelpModalProps) {
  const [customerNote, setCustomerNote] = useState("");
  const [customerPhone, setCustomerPhone] = useState(defaultPhone);
  const [customerName, setCustomerName] = useState(defaultName);
  const [customerEmail, setCustomerEmail] = useState(defaultEmail);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (defaultPhone && !customerPhone) setCustomerPhone(defaultPhone);
  }, [defaultPhone]);

  useEffect(() => {
    if (defaultName && !customerName) setCustomerName(defaultName);
  }, [defaultName]);

  useEffect(() => {
    if (defaultEmail && !customerEmail) setCustomerEmail(defaultEmail);
  }, [defaultEmail]);

  const handleSubmit = async () => {
    if (!customerNote.trim()) {
      setError("Please describe what you need help with.");
      return;
    }
    setError("");
    setSubmitting(true);
    try {
      const requestHelpFn = httpsCallable(functions, "requestOnboardingHelp");
      await requestHelpFn({
        customerNote: customerNote.trim(),
        customerPhone: customerPhone.trim() || null,
        customerName: customerName.trim() || null,
        customerEmail: customerEmail.trim() || null,
        companyId: companyId || null,
        companyName: companyName || null,
        source,
        draftId: draftId || null,
        conversationId: conversationId || null,
      });
      setSubmitted(true);
    } catch (err: any) {
      setError(err?.message || "Failed to submit help request. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const handleResetAndClose = () => {
    setSubmitted(false);
    setCustomerNote("");
    setError("");
    onClose();
  };

  return (
    <Modal
      opened={opened}
      onClose={handleResetAndClose}
      title="Request Help"
      centered
    >
      {submitted ? (
        <Stack spacing="md">
          <Alert color="teal" title="Request Received">
            Your help request has been sent to our support team. We will reach out to you via WhatsApp or phone shortly!
          </Alert>
          <Group position="right">
            <Button color="cap-navy" onClick={handleResetAndClose}>
              Done
            </Button>
          </Group>
        </Stack>
      ) : (
        <Stack spacing="sm">
          <Text size="sm" color="dimmed">
            Tell our team what you need help setting up and how we can reach you on WhatsApp or phone.
          </Text>

          {error && (
            <Alert color="red" title="Error">
              {error}
            </Alert>
          )}

          <Textarea
            required
            label="What do you need help with?"
            placeholder="e.g. Setting up our fuel tanks, importing vehicle list, or customizing a checklist..."
            minRows={3}
            value={customerNote}
            onChange={(e) => setCustomerNote(e.currentTarget.value)}
          />

          <TextInput
            label="Phone / WhatsApp Number"
            placeholder="083 123 4567"
            value={customerPhone}
            onChange={(e) => setCustomerPhone(e.target.value)}
          />

          <Group grow>
            <TextInput
              label="Your Name"
              placeholder="John Doe"
              value={customerName}
              onChange={(e) => setCustomerName(e.target.value)}
            />
            <TextInput
              label="Email"
              placeholder="you@company.com"
              value={customerEmail}
              onChange={(e) => setCustomerEmail(e.target.value)}
            />
          </Group>

          <Group position="right" mt="md">
            <Button variant="subtle" onClick={handleResetAndClose}>
              Cancel
            </Button>
            <Button
              color="cap-navy"
              loading={submitting}
              disabled={!customerNote.trim()}
              onClick={handleSubmit}
            >
              Send Help Request
            </Button>
          </Group>
        </Stack>
      )}
    </Modal>
  );
}
